import React from "react";
import { toast } from "react-toastify";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import RewardsAPI from "api/faber/RewardsAPI";
import MenuClose from "assets/icons/MenuClose.png";
import { AxiosError } from "axios";
import { RoundedButton } from "common/Button/Button";
import NormalText from "common/Typography/NormalText";
import { Form,Formik } from "formik";
import OrbitSession from "hooks/useCookies";
import { DistributorPromocodeDetails } from "types/api/faber/reward.type";
import * as yup from "yup";

interface DatePickerProps {
  label: string;
  disabled: boolean;
  date: Date | null;
  setDate: (value: Date | null) => void;
}

interface RuleRow {
  id: string
  ruleName: string
  ruleDescription: string
  ruleType: string
}

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props;

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        label={label}
        inputFormat="dd-MM-yyyy HH:00"
        views={["year", "month", "day", "hours"]}
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder="Date" variant="standard" {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  );
};

const validationSchema = yup.object({
  maxConsumption: yup
    .number()
    .required("Max consumption is required")
    .min(0, "Cannot be negative"),
  validityEndAt: yup.number().required("Validity end date is required"),
});

interface DistributorUpdateDialogProps {
  handleDialogClose: (value: void) => void;
  openDialog: boolean;
  handleMenuClose: (value: void) => void;
  distributorPromoRow: DistributorPromocodeDetails;
}

const CenteredBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  width: "80%",
});

const CenteredGrid = styled(Grid)({
  display: "flex",
  alignItems: "center",
});

const StyledTextField = styled(TextField)({
  "& input": {
    padding: "6px",
  },
  width: "100%",
});

const ruleColumns: GridColDef[] = [
  {
    field: 'ruleName',
    headerName: 'Rule name',
    width: 350
  },
  {
    field: 'ruleDescription',
    headerName: 'Rule description',
    width: 350
  },
  {
    field: 'ruleType',
    headerName: 'Rule type',
    width: 200
  },
  {
    field: 'ruleValue',
    headerName: 'Rule value',
    width: 200
  }
]

const DistributorUpdateDialog = ({
  handleDialogClose,
  openDialog,
  handleMenuClose,
  distributorPromoRow,
}: DistributorUpdateDialogProps) => {
  const faberToken = OrbitSession.get('faberToken')
  const queryClient = useQueryClient();

  const rewardRulesQuery = useQuery<RuleRow[]>(['getRewardRules'], async () => 
    RewardsAPI.getRewardRules(faberToken)
  )

  const {
    mutate: updateDistributorPromo,
    isLoading: updatingDistributorPromo
  } = useMutation(RewardsAPI.updateDistributorPromocode, {
    onSuccess: () => {
      toast.success('Updated successfully')
    },
    onError: (error) => {
      let errMessage = '';
      if (error instanceof AxiosError) {
        errMessage = error.response?.data.message
      }
      toast.error(errMessage || 'There is a problem while updating campaign promo')
    },
    onSettled: () => {
      queryClient.refetchQueries({queryKey: ['getDistributorPromocodes']})
      dialogClose()
    }
  })

  const dialogClose = () => {
    handleDialogClose();
    handleMenuClose();
  };

  return (
    <Dialog
      open={openDialog}
      onClose={dialogClose}
      fullWidth={true}
      maxWidth="md"
    >
      <Formik
        initialValues={{
          maxClaims: distributorPromoRow.maxClaims || 0,
          maxConsumption: distributorPromoRow.maxConsumption || 0,
          validityStartAt: distributorPromoRow?.validityStartAt || Date.now(),
          validityEndAt: distributorPromoRow?.validityEndAt || Date.now(),
          promoRule: distributorPromoRow?.promoRule
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          updateDistributorPromo({
            id: distributorPromoRow.id,
            ...values,
            faberToken,
          })
        }}
      >
        {({ values, handleChange, errors, touched, setFieldValue }) => (
          <Form>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: "-10px",
                  mt: "-10px",
                }}
              >
                <Typography
                  sx={{ color: "black", fontSize: "18px", fontWeight: "700" }}
                >
                Update Distributor Promo
                </Typography>
                <IconButton
                  onClick={dialogClose}
                  size="small"
                  data-cy="closeDialogBtn"
                  sx={{
                    mr: "-10px",
                    mt: "-10px",
                    width: "40px",
                    height: "40px",
                  }}
                >
                  <img
                    style={{
                      width: "20px",
                      height: "auto",
                    }}
                    src={MenuClose}
                  />
                </IconButton>
              </Box>
            </DialogTitle>
            <Divider sx={{ width: "100%" }} />
            <DialogContent>
              <Grid container rowSpacing={2}>
                <Grid item xs={3}>
                  <NormalText isBold>Claims Per Promo Code</NormalText>
                </Grid>
                <CenteredGrid item xs={9}>
                  <CenteredBox>
                    <StyledTextField
                      name="maxClaims"
                      type="number"
                      inputProps={{
                        min: 1,
                      }}
                      value={values.maxClaims}
                      onChange={handleChange}
                      error={touched.maxClaims && Boolean(errors.maxClaims)}
                      helperText={touched.maxClaims && errors.maxClaims}
                      fullWidth
                      variant="outlined"
                    />
                  </CenteredBox>
                </CenteredGrid>

                <Grid item xs={3}>
                  <NormalText isBold>Consumptions Per Promo Code</NormalText>
                </Grid>
                <CenteredGrid item xs={9}>
                  <CenteredBox>
                    <StyledTextField
                      name="maxConsumption"
                      type="number"
                      inputProps={{
                        min: 1,
                      }}
                      value={values.maxConsumption}
                      onChange={handleChange}
                      error={touched.maxConsumption && Boolean(errors.maxConsumption)}
                      helperText={touched.maxConsumption && errors.maxConsumption}
                      fullWidth
                      variant="outlined"
                    />
                  </CenteredBox>
                </CenteredGrid>

                <Grid item xs={3}>
                  <NormalText isBold>Validity Start Date</NormalText>
                </Grid>
                <Grid item xs={9}>
                  <CustomDatePicker
                    label="Validity Start Date"
                    date={new Date(values.validityStartAt)}
                    setDate={(value) => {
                      const unixTime = value ? value.getTime() : null;
                      setFieldValue("validityStartAt", unixTime);
                    }}
                    disabled={false}
                  />
                </Grid>

                <Grid item xs={3}>
                  <NormalText isBold>Validity End Date</NormalText>
                </Grid>
                <Grid item xs={9}>
                  <CustomDatePicker
                    label="Validity End Date"
                    date={new Date(values.validityEndAt)}
                    setDate={(value) => {
                      const unixTime = value ? value.getTime() : null;
                      setFieldValue("validityEndAt", unixTime);
                    }}
                    disabled={false}
                  />
                </Grid>

                <Grid item xs={3}>
                  <NormalText isBold>Reward Rules</NormalText>
                </Grid>
                <Grid item xs={12} height={400}>
                  {rewardRulesQuery.isLoading ? (
                    <span>Loading...</span>
                  ) : rewardRulesQuery.data && (
                    <DataGrid
                      rows={rewardRulesQuery.data}
                      columns={ruleColumns}
                      checkboxSelection
                      rowsPerPageOptions={[5]}
                      pageSize={5}
                      selectionModel={values.promoRule}
                      onSelectionModelChange={(newSelection) => {
                        setFieldValue("promoRule", newSelection)
                      }}
                      sx={{
                        '& .MuiDataGrid-cell': {
                          pointerEvents: 'none', // Disables all mouse events
                        },
                        '& .MuiDataGrid-cellCheckbox': {
                          pointerEvents: 'auto', // Re-enables for checkboxes
                        }
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <RoundedButton
                variant="secondary"
                onClick={dialogClose}
                disabled={updatingDistributorPromo}
              >
                Cancel
              </RoundedButton>
              <RoundedButton
                variant="filled"
                type="submit"
                disabled={updatingDistributorPromo}
              >
                Submit
              </RoundedButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default DistributorUpdateDialog;
